import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import CandidateTabs from './CandidateTabs';
import CandidateDecisionHeader from './candidate/CandidateDecisionHeader';
import InterviewEvaluationModal from './InterviewEvaluationModal';
import PhoneScreeningModal from './PhoneScreeningModal';
import FileViewerModal from './FileViewerModal';
import { downloadCandidateAnalysis } from '../utils/downloadAnalysis';
import { downloadJobDescription } from '../utils/downloadJobDescription';
import {
    questionnaireHasAnyQuestions,
    parseQuestionnaireFromCandidate,
    normalizeInterviewEvaluationResults,
} from '../utils/interviewEvalUtils';
import { resolveCandidateContact } from '../utils/candidateAnalysisUtils';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './CandidateRow.css';

const CandidateRow = ({ candidate, rank, isExpanded, isFocused, activeTab, onExpand, onTabChange, jobTitle: propJobTitle, jobCompanyName, jobDescription, onQuestionnaireSaved, onEvaluateInterview, onScheduleInterview, onEvaluationSaved, onPhoneScreeningSaved, isSelected, onToggleSelect }) => {
    const { jobId } = useParams();
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [resumeContent, setResumeContent] = useState('');
    const [resumeFilename, setResumeFilename] = useState('');
    const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
    const [isInterviewEvaluationModalOpen, setIsInterviewEvaluationModalOpen] = useState(false);
    /** When job refetch returns fresher resume (eval results), use for modal props until close. */
    const [modalCandidateSnapshot, setModalCandidateSnapshot] = useState(null);
    const [interviewQuestionnaire, setInterviewQuestionnaire] = useState(null);
    const [isGeneratingQuestionnaire, setIsGeneratingQuestionnaire] = useState(false);
    const abortControllerRef = useRef(null); // Store abort controller for cancellation
    const modalClosedDuringGenerationRef = useRef(false); // True when user closed modal (X or Back) while generating
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedResumeForSchedule, setSelectedResumeForSchedule] = useState(null);
    const [isPhoneScreeningOpen, setIsPhoneScreeningOpen] = useState(false);
    // Extract score (prioritize calibrated_score > fit_score > legacy score)
    const getScore = () => {
        if (candidate.rag_analysis?.evaluation_result?.calibrated_score !== undefined) {
            return {
                value: candidate.rag_analysis.evaluation_result.calibrated_score,
                label: 'CALIBRATED SCORE',
                source: 'calibrated'
            };
        } else if (candidate.rag_analysis?.evaluation_result?.fit_score !== undefined) {
            return {
                value: candidate.rag_analysis.evaluation_result.fit_score,
                label: 'FIT SCORE',
                source: 'fit'
            };
        } else if (candidate.analysis?.fit_score !== undefined) {
            return {
                value: candidate.analysis.fit_score,
                label: 'FIT SCORE',
                source: 'legacy'
            };
        } else if (candidate.analysis?.score !== undefined) {
            return {
                value: candidate.analysis.score,
                label: 'SCORE',
                source: 'legacy'
            };
        }
        return null;
    };

    // Get eligibility status
    const getEligibilityStatus = () => {
        if (candidate.rag_analysis?.evaluation_result?.eligibility_status) {
            const status = candidate.rag_analysis.evaluation_result.eligibility_status;
            if (status === 'eligible') {
                return { label: 'Eligible', color: 'green', icon: '✅' };
            } else if (status === 'ineligible') {
                return { label: 'Not Eligible', color: 'red', icon: '⚠️' };
            } else {
                return { label: 'Needs Review', color: 'yellow', icon: '🟡' };
            }
        } else if (candidate.analysis?.bucket) {
            const bucket = candidate.analysis.bucket.toLowerCase();
            if (bucket.includes('top-tier') || bucket.includes('strong-fit') || bucket.includes('good-fit')) {
                return { label: 'Eligible', color: 'green', icon: '✅' };
            } else if (bucket.includes('not-a-fit') || bucket.includes('lacks-experience')) {
                return { label: 'Not Eligible', color: 'red', icon: '⚠️' };
            } else {
                return { label: 'Needs Review', color: 'yellow', icon: '🟡' };
            }
        }
        return { label: 'Unknown', color: 'gray', icon: '❓' };
    };

    // Get decision outcome (replaces eligibility badge)
    const getDecisionOutcome = () => {
        const scoreValue = score?.value || 0;
        const eligibility = getEligibilityStatus();
        
        // Check for hard gate failures (ineligible)
        const hardGateFailures = candidate.rag_analysis?.evaluation_result?.gate_evaluation_result?.hard_gate_failures || [];
        const softGateFailures = candidate.rag_analysis?.evaluation_result?.gate_evaluation_result?.soft_gate_failures || [];
        
        if (eligibility.color === 'red' || hardGateFailures.length > 0) {
            return {
                label: 'Do Not Advance',
                color: 'red',
                tooltip: 'Candidate does not meet minimum requirements. Hard gate failures detected.'
            };
        } else if (eligibility.color === 'yellow' || softGateFailures.length >= 3 || scoreValue < 60) {
            return {
                label: 'Advance with Gaps',
                color: 'yellow',
                tooltip: `Candidate has ${softGateFailures.length} soft gate failures or gaps. Proceed with caution.`
            };
        } else if (scoreValue >= 75 && eligibility.color === 'green') {
            return {
                label: 'Advance to Interview',
                color: 'green',
                tooltip: 'Strong candidate. Meets all requirements and has high fit score.'
            };
        } else if (scoreValue >= 60) {
            return {
                label: 'Advance with Gaps',
                color: 'yellow',
                tooltip: 'Candidate meets basic requirements but has some gaps. Review carefully.'
            };
        } else {
            return {
                label: 'Do Not Advance',
                color: 'red',
                tooltip: 'Low fit score. Does not meet minimum requirements.'
            };
        }
    };

    // Get score interpretation
    const getScoreInterpretation = (scoreValue) => {
        if (scoreValue >= 80) return 'Proceed';
        if (scoreValue >= 65) return 'Proceed with Caution';
        if (scoreValue >= 50) return 'Review Required';
        return 'Do Not Proceed';
    };

    // Get supporting signals (confidence, benchmark, evidence quality)
    const getSupportingSignals = () => {
        const ragAnalysis = typeof candidate.rag_analysis === 'string' 
            ? JSON.parse(candidate.rag_analysis || '{}') 
            : (candidate.rag_analysis || {});
        const evalResult = ragAnalysis?.evaluation_result || {};
        
        return {
            confidence: evalResult.evidence_confidence !== undefined 
                ? Math.round(evalResult.evidence_confidence * 100) 
                : null,
            benchmark: evalResult.calibration_metadata?.benchmark_percentile !== undefined
                ? Math.round(evalResult.calibration_metadata.benchmark_percentile * 100)
                : null,
            evidenceQuality: evalResult.evidence_quality_metrics?.overall_quality !== undefined
                ? Math.round(evalResult.evidence_quality_metrics.overall_quality * 100)
                : (evalResult.evidence_quality_metrics?.citation_coverage !== undefined
                    ? Math.round(evalResult.evidence_quality_metrics.citation_coverage * 100)
                    : null)
        };
    };

    // Get bucket (enterprise-friendly language)
    const getBucket = () => {
        if (candidate.rag_analysis?.evaluation_result?.eligibility_status) {
            const status = candidate.rag_analysis.evaluation_result.eligibility_status;
            if (status === 'eligible') {
                return 'Strong Prospect';
            } else if (status === 'ineligible') {
                return 'Not Aligned';
            } else {
                return 'Hold';
            }
        } else if (candidate.analysis?.bucket) {
            const bucket = candidate.analysis.bucket;
            // Convert to enterprise-friendly terms
            if (bucket.toLowerCase().includes('top-tier')) {
                return 'Strong Prospect';
            } else if (bucket.toLowerCase().includes('strong-fit') || bucket.toLowerCase().includes('good-fit')) {
                return 'Good Fit';
            } else if (bucket.toLowerCase().includes('not-a-fit')) {
                return 'Not Aligned';
            } else if (bucket.toLowerCase().includes('lacks-experience')) {
                return 'Insufficient Experience';
            }
            return bucket;
        }
        return 'Unknown';
    };

    // Get top skills (max 2)
    const getTopSkills = () => {
        const skills = [];
        
        if (candidate.analysis?.skill_matrix?.matching_skills) {
            const matching = candidate.analysis.skill_matrix.matching_skills;
            skills.push(...(matching.matching_hard_skills || []));
            skills.push(...(matching.matching_soft_skills || []));
            skills.push(...(matching.matching_tools_technologies_frameworks || []));
            skills.push(...(matching.matching_languages || []));
        } else if (candidate.analysis?.skill_matrix?.matches) {
            skills.push(...candidate.analysis.skill_matrix.matches);
        }
        
        return skills.slice(0, 2);
    };

    // Get gaps/flags (max 2)
    const getGaps = () => {
        const gaps = [];
        
        if (candidate.analysis?.skill_matrix?.missing_job_skills) {
            const missing = candidate.analysis.skill_matrix.missing_job_skills;
            gaps.push(...(missing.missing_hard_skills || []));
            gaps.push(...(missing.missing_soft_skills || []));
            gaps.push(...(missing.missing_tools_technologies_frameworks || []));
            gaps.push(...(missing.missing_languages || []));
        } else if (candidate.analysis?.skill_matrix?.gaps) {
            gaps.push(...candidate.analysis.skill_matrix.gaps);
        }
        
        return gaps.slice(0, 2);
    };

    // Get capability score (0-100)
    const getCapabilityScore = () => {
        const ragAnalysis = typeof candidate.rag_analysis === 'string' 
            ? JSON.parse(candidate.rag_analysis || '{}') 
            : (candidate.rag_analysis || {});
        const evalResult = ragAnalysis?.evaluation_result || {};
        
        // Try to get capability from subfield scores or use fit score as proxy
        if (evalResult.capability_score !== undefined) {
            return evalResult.capability_score * 100;
        }
        
        // Fallback: estimate from fit score
        const fitScore = score?.value || 0;
        return Math.max(0, fitScore - 10); // Slightly lower than fit
    };

    // Get fit score (0-100)
    const getFitScore = () => {
        return score?.value || 0;
    };

    const score = getScore();
    const eligibility = getEligibilityStatus();
    const bucket = getBucket();
    const topSkills = getTopSkills();
    const gaps = getGaps();
    const contact = resolveCandidateContact(candidate);
    const candidateName = contact.name || candidate.filename || 'Unknown Candidate';
    const decisionOutcome = getDecisionOutcome();
    const supportingSignals = getSupportingSignals();
    const scoreInterpretation = score ? getScoreInterpretation(score.value) : null;
    
    // Get job title from prop or fallback
    const jobTitle = propJobTitle || candidate.job_title || 'Applied Role';

    const capabilityScore = getCapabilityScore();
    const fitScore = getFitScore();

    // Handle viewing resume in modal
    const handleViewResume = async () => {
        try {
            const response = await apiFetch(`/api/resumes/${candidate.id}/file`, {
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        errorData,
                        `Failed to fetch resume file (${response.status})`
                    )
                );
            }
            const data = await response.json();
            setResumeContent(data.content);
            setResumeFilename(data.filename);
            setIsResumeModalOpen(true);
        } catch (error) {
            console.error('Error fetching resume file:', error);
            alert(`Failed to load resume: ${error.message}. Please try again.`);
        }
    };

    const handleCloseResumeModal = () => {
        setIsResumeModalOpen(false);
        setResumeContent('');
        setResumeFilename('');
    };

    const resumeForInterviewModal = modalCandidateSnapshot || candidate;

    // Whether this candidate has a saved questionnaire (show "Evaluate Interview" vs "Conduct Interview")
    const hasQuestionnaire = (() => {
        if (!candidate.questionnaire) return false;
        try {
            const q = typeof candidate.questionnaire === 'string'
                ? JSON.parse(candidate.questionnaire)
                : candidate.questionnaire;
            return q && (
                (q.introduction && q.introduction.length > 0) ||
                (q.technical && (q.technical.theoretical?.length > 0 || q.technical.practical?.length > 0)) ||
                (q.behavioral && q.behavioral.length > 0) ||
                (q.situational && q.situational.length > 0)
            );
        } catch (e) {
            return false;
        }
    })();

    // Handle conducting interview (generate questions)
    const handleConductInterview = async () => {
        if (isGeneratingQuestions) return;

        modalClosedDuringGenerationRef.current = false;
        setIsGeneratingQuestionnaire(false);
        setIsGeneratingQuestions(false);
        setModalCandidateSnapshot(null);

        // Always refresh job from server before opening so evaluation_completed / evaluation_results /
        // evaluation_payload match Postgres (list row can be stale until poll — fixes result card vs questions).
        let effectiveCandidate = candidate;
        let fetchedJobDescription = jobDescription || '';
        if (jobId) {
            try {
                const jobResponse = await apiFetch(`/api/jobs/${jobId}`);
                if (jobResponse.ok) {
                    const jobData = await jobResponse.json();
                    if (jobData?.description) {
                        fetchedJobDescription = jobData.description;
                    }
                    const cid = Number(candidate.id);
                    const fresh = jobData?.resumes?.find((r) => Number(r.id) === cid);
                    if (fresh) {
                        effectiveCandidate = fresh;
                        setModalCandidateSnapshot(fresh);
                    }
                }
            } catch (_) { /* keep effectiveCandidate = candidate */ }
        }

        // Prefer resume.questionnaire, then questionnaire stored inside evaluation_payload (saved at evaluate time).
        const parsedQ = parseQuestionnaireFromCandidate(effectiveCandidate);
        if (parsedQ) {
            setInterviewQuestionnaire(parsedQ);
        } else {
            setInterviewQuestionnaire(null);
        }

        setIsInterviewEvaluationModalOpen(true);

        // Load questionnaire in background only when we still don't have one (e.g. first visit).
        if (!parsedQ) {
            (async () => {
                let jobDesc = fetchedJobDescription;
                if (!jobDesc && jobId) {
                    try {
                        const jobResponse = await apiFetch(`/api/jobs/${jobId}`);
                        if (jobResponse.ok) {
                            const jobData = await jobResponse.json();
                            jobDesc = jobData.description || '';
                        }
                    } catch (_) { /* ignore */ }
                }
                if (!jobDesc) return;
                try {
                    const loadResponse = await apiFetch('/api/generate-questionnaire', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            resume_id: candidate.id,
                            job_description: jobDesc,
                            candidate_name: candidate.candidate_name || candidate.filename || 'Candidate',
                            fit_score: (getScore()?.value ?? 0),
                            force_new: false
                        })
                    });
                    const data = await loadResponse.json().catch(() => ({}));
                    let q = data?.questionnaire;
                    if (loadResponse.status === 202 && data.status === 'processing' && data.resume_id) {
                        const deadline = Date.now() + 3 * 60 * 1000;
                        while (Date.now() < deadline) {
                            const statusRes = await apiFetch(`/api/resumes/${data.resume_id}/questionnaire-status`);
                            const statusBody = await statusRes.json().catch(() => ({}));
                            if (statusBody.ready && statusBody.questionnaire) {
                                q = statusBody.questionnaire;
                                break;
                            }
                            await new Promise(r => setTimeout(r, 2500));
                        }
                    }
                    if (questionnaireHasAnyQuestions(q)) setInterviewQuestionnaire(q);
                } catch (_) { /* ignore */ }
            })();
        }
    };

    // Separate function for generating questionnaire (called from choice mode)
    const handleGenerateQuestionnaire = async () => {
        if (isGeneratingQuestions) return;
        
        console.log('📋 [Generate Questionnaire] Starting new questionnaire generation for new evaluation');
        console.log('📋 [Generate Questionnaire] Current interviewQuestionnaire before clear:', interviewQuestionnaire);
        
        // Clear existing questionnaire to ensure fresh generation
        setInterviewQuestionnaire(null);
        
        // Small delay to ensure state is cleared before proceeding
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setIsGeneratingQuestions(true);
        setIsGeneratingQuestionnaire(true);
        try {
            // Get job description if not provided
            let jobDesc = jobDescription;
            if (!jobDesc && jobId) {
                const jobResponse = await apiFetch(`/api/jobs/${jobId}`, {
                });
                if (jobResponse.ok) {
                    const jobData = await jobResponse.json();
                    jobDesc = jobData.description || '';
                }
            }

            if (!jobDesc) {
                throw new Error('Job description not available. Please ensure the job has a description.');
            }

            // Show the interview evaluation UI immediately with a guaranteed intro question,
            // then generate the full questionnaire in the background (with timeout + retries).
            const hardcodedIntro = {
                id: 'introduction_0',
                question: 'Please walk us through your professional background, highlighting the key experiences and skills that align with this role. What motivates you to pursue this position, and how do you believe your unique combination of expertise makes you the ideal candidate?',
                expected_answer_summary: [
                    'Clear articulation of relevant professional background',
                    'Specific examples of experiences that align with the role',
                    'Demonstration of motivation and passion for the position',
                    'Unique value proposition and expertise combination',
                    'Understanding of role requirements and how background fits'
                ],
                weightage: 10
            };

            const hardcodedQuestionnaire = {
                introduction: [hardcodedIntro],
                behavioral: [],
                technical: { theoretical: [], practical: [] },
                situational: []
            };

            // Clear cache to force new questionnaire generation (don't reuse old questionnaire)
            const requestKey = `candidate_questionnaire_${candidate.id}`;
            if (window[requestKey]) {
                delete window[requestKey];
                console.log('🔄 [Generate Questionnaire] Cleared cached questionnaire for new evaluation');
            }
            
            // Set temporary questionnaire with intro question while generating
            setInterviewQuestionnaire(hardcodedQuestionnaire);

            const POLL_INTERVAL_MS = 2500;
            const POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

            const pollQuestionnaireStatus = async (resumeId) => {
                const deadline = Date.now() + POLL_TIMEOUT_MS;
                while (Date.now() < deadline) {
                    if (abortControllerRef.current?.signal.aborted) throw new Error('__cancelled__');
                    const res = await apiFetch(`/api/resumes/${resumeId}/questionnaire-status`);
                    const body = await res.json().catch(() => ({}));
                    if (body.ready && body.questionnaire) return body.questionnaire;
                    if (body.error && res.status >= 400) throw new Error(body.error || 'Poll failed');
                    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
                }
                throw new Error('Questionnaire generation timed out. Please try again.');
            };

            const generateQuestionnaireWithRetry = async (maxRetries = 3) => {
                const makeRequest = async (attempt = 1) => {
                    if (abortControllerRef.current) abortControllerRef.current.abort();
                    const controller = new AbortController();
                    abortControllerRef.current = controller;
                    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s for POST only (background)

                    try {
                        const requestPayload = {
                            resume_id: candidate.id,
                            job_description: jobDesc,
                            candidate_name: candidate.candidate_name || candidate.filename || 'Candidate',
                            fit_score: score?.value || 0,
                            force_new: true
                        };
                        console.log('📋 [Generate Questionnaire] Sending request (background):', requestPayload);

                        const response = await apiFetch('/api/generate-questionnaire', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(requestPayload),
                            signal: controller.signal
                        });

                        clearTimeout(timeoutId);

                        const responseData = await response.json().catch(() => ({}));

                        if (response.status === 202 && responseData.status === 'processing') {
                            console.log('📋 [Generate Questionnaire] Background started, polling for result...');
                            const questionnaire = await pollQuestionnaireStatus(candidate.id);
                            return { questionnaire, from_cache: false, message: 'Generated' };
                        }

                        if (!response.ok) {
                            const err = new Error(
                                getUserFacingErrorMessage(
                                    response.status,
                                    responseData,
                                    `HTTP ${response.status}`
                                )
                            );
                            err.status = response.status;
                            throw err;
                        }

                        if (responseData.questionnaire) {
                            return responseData;
                        }

                        const noQ = new Error(
                            getUserFacingErrorMessage(
                                response.status,
                                responseData,
                                responseData.error || 'No questionnaire in response'
                            )
                        );
                        noQ.status = response.status;
                        throw noQ;
                    } catch (error) {
                        clearTimeout(timeoutId);
                        if (error.name === 'AbortError') {
                            console.log('🛑 [Generate Questionnaire] Request aborted');
                            throw new Error('__cancelled__');
                        }
                        if (
                            error.message?.includes('HTTP 4') ||
                            (error.status >= 400 && error.status < 500)
                        ) {
                            throw error;
                        }
                        if (attempt < maxRetries) {
                            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
                            await new Promise(resolve => setTimeout(resolve, delay));
                            return makeRequest(attempt + 1);
                        }
                        throw error;
                    }
                };

                window[requestKey] = makeRequest();
                try {
                    const data = await window[requestKey];
                    delete window[requestKey];
                    return data;
                } catch (e) {
                    delete window[requestKey];
                    throw e;
                }
            };

            const data = await generateQuestionnaireWithRetry();
            console.log('📋 [Generate Questionnaire] Data received:', {
                from_cache: data?.from_cache,
                questionnaire_sections: data?.questionnaire ? Object.keys(data.questionnaire) : []
            });

            const mergedQuestionnaire = {
                ...(data?.questionnaire || {}),
                introduction: [hardcodedIntro]
            };
            console.log('📋 [Generate Questionnaire] Merged questionnaire sections:', Object.keys(mergedQuestionnaire));

            // Persist to DB so CandidateListPage refresh shows questions in Interview tab
            try {
                const saveResponse = await apiFetch('/api/save-questionnaire', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        resume_id: candidate.id,
                        questionnaire: mergedQuestionnaire
                    })
                });

                if (!saveResponse.ok) {
                    const errorText = await saveResponse.text().catch(() => '');
                    console.warn('Failed to save questionnaire:', saveResponse.status, errorText);
                }
            } catch (saveErr) {
                console.warn('Failed to save questionnaire:', saveErr);
            }

            // Update the open modal immediately (parent list will refresh from DB on its interval)
            setInterviewQuestionnaire(mergedQuestionnaire);
            setIsGeneratingQuestionnaire(false);

            if (modalClosedDuringGenerationRef.current) {
                modalClosedDuringGenerationRef.current = false;
                alert('Interview questions have been generated. Open Conduct Interview again to use them.');
            }
        } catch (error) {
            console.error('Error generating interview questions:', error);
            const msg = error.message || '';
            if (msg === '__cancelled__' || msg.includes('cancelled')) return;
            alert(`Failed to generate interview questions: ${msg || 'Unknown error'}. Please try again.`);
        } finally {
            setIsGeneratingQuestions(false);
        }
    };

    // Handle schedule interview
    const handleScheduleInterview = (resume) => {
        setSelectedResumeForSchedule(resume);
        setIsScheduleModalOpen(true);
    };

    const handleCloseScheduleModal = () => {
        setIsScheduleModalOpen(false);
        setSelectedResumeForSchedule(null);
    };

    const handleScheduleSubmit = async (interviewData) => {
        try {
            const response = await apiFetch('/api/google-schedule-interview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(interviewData)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(
                    getUserFacingErrorMessage(response.status, errData, 'Failed to schedule interview')
                );
            }
        } finally {
            // Reset BOTH state variables to ensure button and modal state are cleared
            setIsGeneratingQuestions(false);
            setIsGeneratingQuestionnaire(false);
            // Clear abort controller reference
            abortControllerRef.current = null;
        }
    };
    
    // Function to cancel ongoing questionnaire generation
    const handleCancelQuestionnaireGeneration = () => {
        console.log('🛑 [Cancel] Cancelling questionnaire generation');
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        // Reset BOTH state variables to ensure button and modal state are cleared
        setIsGeneratingQuestionnaire(false);
        setIsGeneratingQuestions(false);
        setInterviewQuestionnaire(null);
        // Clear the cached request
        const requestKey = `candidate_questionnaire_${candidate.id}`;
        if (window[requestKey]) {
            delete window[requestKey];
        }
        console.log('🛑 [Cancel] All generation states reset, button should now show "Conduct Interview"');
    };

    return (
        <div className={`candidate-row ${isExpanded ? 'expanded' : 'collapsed'} ${isFocused ? 'focused' : ''}`}>
            {/* Collapsed State - Compact, Scannable Design */}
            {!isExpanded && (
                <div className="candidate-row-collapsed" onClick={onExpand}>
                    {/* Selection Checkbox */}
                    {onToggleSelect && (
                        <input
                            type="checkbox"
                            className="candidate-select-checkbox"
                            checked={!!isSelected}
                            onChange={(e) => { e.stopPropagation(); onToggleSelect(candidate.id); }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    {/* Rank */}
                    <div className="candidate-rank">{rank}</div>
                    
                    {/* Name + Status */}
                    <div className="candidate-name-section">
                        <div className="candidate-name-collapsed">{candidateName}</div>
                        <div className="candidate-status-label">{bucket}</div>
                    </div>
                    
                    {/* Score + Eligibility */}
                    <div className="candidate-score-section">
                        {score && (
                            <div className={`candidate-score-pill score-${score.source}`}>
                                {Math.round(score.value)}
                            </div>
                        )}
                        <div className={`eligibility-badge eligibility-${eligibility.color}`}>
                            {eligibility.color === 'green' ? '✓' : eligibility.color === 'red' ? '✗' : '⚠'} {eligibility.label}
                        </div>
                    </div>
                    
                    {/* Capability vs Fit Micro-bars */}
                    <div className="candidate-bars-section">
                        <div className="micro-bar-group">
                            <span className="bar-label">Fit</span>
                            <div className="micro-bar">
                                <div 
                                    className="micro-bar-fill fit-bar"
                                    style={{ width: `${fitScore}%` }}
                                ></div>
                            </div>
                        </div>
                        <div className="micro-bar-group">
                            <span className="bar-label">Capability</span>
                            <div className="micro-bar">
                                <div 
                                    className="micro-bar-fill capability-bar"
                                    style={{ width: `${capabilityScore}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Strengths / Gaps Chips */}
                    <div className="candidate-chips-section">
                        {topSkills.length > 0 && (
                            <div className="chips-group">
                                <span className="chip-label">Skills:</span>
                                {topSkills.slice(0, 2).map((skill, idx) => (
                                    <span key={idx} className="skill-chip">{skill}</span>
                                ))}
                            </div>
                        )}
                        {gaps.length > 0 && (
                            <div className="chips-group">
                                <span className="chip-label">Gaps:</span>
                                {gaps.slice(0, 2).map((gap, idx) => (
                                    <span key={idx} className="gap-chip">{gap}</span>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Chevron */}
                    <div className="candidate-expand-icon">›</div>
                </div>
            )}

            {/* Expanded State */}
            {isExpanded && (
                <div className="candidate-row-expanded">
                    {/* Premium Decision Header */}
                    <CandidateDecisionHeader
                        candidateName={candidateName}
                        roleTitle={jobTitle}
                        jobTitle={jobTitle}
                        calibratedScore={score?.value}
                        statusLabel={(() => {
                            // Map decision outcome to status label format
                            const label = decisionOutcome.label;
                            if (label === 'Advance to Interview') return 'Eligible';
                            if (label === 'Advance with Gaps') return 'Proceed with Caution';
                            if (label === 'Do Not Advance') return 'Not Recommended';
                            return label;
                        })()}
                        confidence={supportingSignals.confidence !== null ? supportingSignals.confidence : undefined}
                        benchmarkPercentile={supportingSignals.benchmark !== null ? supportingSignals.benchmark : undefined}
                        evidenceQuality={supportingSignals.evidenceQuality !== null ? supportingSignals.evidenceQuality : undefined}
                        scoreSource={(() => {
                            const ragAnalysis = typeof candidate.rag_analysis === 'string' 
                                ? JSON.parse(candidate.rag_analysis || '{}') 
                                : (candidate.rag_analysis || {});
                            return ragAnalysis?.evaluation_result?.resolved_scoring_profile ? 'Resolved Profile' : 'JD-weighted';
                        })()}
                        lastEvaluated={candidate.updated_at || candidate.created_at}
                        evaluationMode={(() => {
                            const ragAnalysis = typeof candidate.rag_analysis === 'string' 
                                ? JSON.parse(candidate.rag_analysis || '{}') 
                                : (candidate.rag_analysis || {});
                            return ragAnalysis?.evaluation_result?.evaluation_mode;
                        })()}
                        onScheduleInterview={onScheduleInterview ? () => onScheduleInterview(candidate) : undefined}
                        onPhoneScreening={() => setIsPhoneScreeningOpen(true)}
                        phoneScreeningCompleted={
                            String(candidate?.phone_screening?.status || '').toLowerCase() === 'completed'
                        }
                        onDownloadJD={jobId ? () => {
                            downloadJobDescription({ jobId, jobTitle });
                        } : undefined}
                        onViewResume={handleViewResume}
                        onDownloadAnalysis={() => {
                            downloadCandidateAnalysis(candidate);
                        }}
                        onConductInterview={handleConductInterview}
                        onEvaluateInterview={onEvaluateInterview ? () => onEvaluateInterview(candidate) : undefined}
                        hasQuestionnaire={hasQuestionnaire}
                        isGeneratingQuestions={isGeneratingQuestions}
                    />

                    {/* Tabs */}
                    <CandidateTabs
                        candidate={candidate}
                        activeTab={activeTab}
                        onTabChange={onTabChange}
                    />

                    {/* Interview Evaluation Modal (Conduct Interview flow) */}
                    <InterviewEvaluationModal
                        isOpen={isInterviewEvaluationModalOpen}
                        onClose={() => {
                            console.log('🚪 [Modal Close] Closing modal (generation continues in background if in progress)');
                            if (isGeneratingQuestionnaire) modalClosedDuringGenerationRef.current = true;
                            setIsInterviewEvaluationModalOpen(false);
                            setIsGeneratingQuestionnaire(false);
                            setIsGeneratingQuestions(false);
                            setInterviewQuestionnaire(null);
                            setModalCandidateSnapshot(null);
                        }}
                        questionnaire={interviewQuestionnaire}
                        evaluationCompleted={Boolean(
                            resumeForInterviewModal?.evaluation_completed ||
                                normalizeInterviewEvaluationResults(resumeForInterviewModal)
                        )}
                        evaluationResults={normalizeInterviewEvaluationResults(resumeForInterviewModal)}
                        evaluationPayload={resumeForInterviewModal?.evaluation_payload}
                        candidateName={candidateName}
                        fitScore={score?.value || 0}
                        jobTitle={jobTitle}
                        resumeId={resumeForInterviewModal?.id ?? candidate.id}
                        onGenerateQuestionnaire={handleGenerateQuestionnaire}
                        onCancelQuestionnaireGeneration={handleCancelQuestionnaireGeneration}
                        isGeneratingQuestionnaire={isGeneratingQuestionnaire}
                        onEvaluationSaved={onEvaluationSaved}
                        jobDescription={jobDescription}
                        jobId={jobId ? parseInt(jobId, 10) : null}
                        candidateEmail={contact.email}
                        jobCompanyName={jobCompanyName || null}
                    />

                    {/* Phone Screening Modal */}
                    <PhoneScreeningModal
                        isOpen={isPhoneScreeningOpen}
                        onClose={() => setIsPhoneScreeningOpen(false)}
                        candidate={candidate}
                        jobId={jobId ? parseInt(jobId, 10) : candidate?.job_id}
                        onSaved={(data) => {
                            if (onPhoneScreeningSaved) onPhoneScreeningSaved(data);
                        }}
                    />

                    {/* Resume Modal */}
                    <FileViewerModal
                        isOpen={isResumeModalOpen}
                        onClose={handleCloseResumeModal}
                        content={resumeContent}
                        filename={resumeFilename}
                    />
                </div>
            )}
        </div>
    );
};

export default CandidateRow;
